import { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef, useMemo, type ReactNode } from 'react';
import Map, { Source, Layer, GeolocateControl, NavigationControl } from 'react-map-gl/mapbox';
import type { MapRef, MapMouseEvent } from 'react-map-gl/mapbox';
import type { Map as MapboxMap } from 'mapbox-gl';
import { MAPBOX_STYLE } from '@/config/mapbox';
import { useMapboxDraw } from '@/hooks/useMapboxDraw';
import { TurfStatus } from '@signalvote/shared';
import type { Turf, VoterHouse, GeoJSONPolygon } from '@signalvote/shared';
import { DemographicOverlay } from './DemographicOverlay';
import type { DemographicMetric } from './DemographicOverlay';
import { RoadClassOverlay } from './RoadClassOverlay';
import { RoadClassLegend } from './RoadClassLegend';
import { ElectionOverlay } from './ElectionOverlay';
import { CanvasserPopup } from './CanvasserPopup';
import { TurfVertexOverlay } from './TurfVertexOverlay';
import { TURF_STATUS_DISPLAY, TURF_STATUS_FALLBACK_HEX } from './turfStatusDisplay';
import { MAP_COLORS } from './mapColors';
import type { ContactRateDto } from '@signalvote/shared';

// ---------- Default map center (Birmingham, AL) ----------

export const DEFAULT_MAP_CENTER = {
  longitude: -86.8,
  latitude: 33.4985,
  zoom: 15.5,
} as const;

// ---------- Contact rate overlay colors (VMF-007) ----------

export const CONTACT_RATE_COLORS = {
  red: '#ef4444',       // < 25%
  yellow: '#eab308',    // 25-50%
  green: '#22c55e',     // 50-80%
  darkGreen: '#15803d', // 80%+
  gray: '#9ca3af',      // no data
} as const;

const CONTACT_RATE_THRESHOLDS = {
  low: 0.25,
  medium: 0.50,
  high: 0.80,
} as const;

// ---------- Types ----------

export interface TurfMapProps {
  token: string;
  turfs: Turf[];
  getTurfStatus: (id: string) => TurfStatus;
  houses: VoterHouse[];
  selectedTurfId: string | null;
  onTurfClick: (turfId: string) => void;
  canvasserPositions?: GeoJSON.FeatureCollection;
  drawingEnabled?: boolean;
  initialDrawPolygon?: GeoJSONPolygon | null;
  onPolygonComplete?: (polygon: GeoJSONPolygon) => void;
  onPolygonEdit?: (polygon: GeoJSONPolygon) => void;
  mode?: 'management' | 'cutting';
  voterPoints?: Array<{ id: string; coordinate: [number, number]; isContacted: boolean }>;
  showVoterPins?: boolean;
  showTurfs?: boolean;
  showDemographics?: boolean;
  demographicMetric?: DemographicMetric;
  showElectionOverlay?: boolean;
  showMajorRoads?: boolean;
  showContactRate?: boolean;
  contactRates?: ContactRateDto[];
  onVoterClick?: (voterIds: string[], lngLat: [number, number]) => void;
  children?: ReactNode;
}

export interface TurfMapHandle {
  fitToTurf: (turfId: string) => void;
  fitToAll: () => void;
  clearDraw: () => void;
}

// ---------- Layer IDs ----------

const TURF_FILL_LAYER = 'turf-fill';
const TURF_LINE_LAYER = 'turf-line';
const TURF_LABEL_LAYER = 'turf-labels';
const HOUSES_LAYER = 'houses';
const CANVASSER_HALO_LAYER = 'canvasser-halo';
const CANVASSER_LAYER = 'canvassers';
const CANVASSER_LABEL_LAYER = 'canvasser-labels';
const VOTER_HEAT_LAYER = 'voter-heat';
const VOTER_DOTS_LAYER = 'voter-dots';

const VOTER_DOTS_CONTACTED_LAYER = 'voter-dots-contacted';
const INTERACTIVE_LAYERS = [TURF_FILL_LAYER, VOTER_DOTS_LAYER, VOTER_DOTS_CONTACTED_LAYER, CANVASSER_LAYER];

// ---------- Helpers ----------

/**
 * Compute a [west, south, east, north] bounding box from an array
 * of GeoJSON Polygon coordinate rings.
 */
function computeBounds(
  turfs: Turf[],
): [number, number, number, number] | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let hasCoords = false;

  for (const turf of turfs) {
    for (const ring of turf.polygon.coordinates) {
      for (const coord of ring) {
        const lng = coord[0];
        const lat = coord[1];
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        hasCoords = true;
      }
    }
  }

  if (!hasCoords) return null;
  return [minLng, minLat, maxLng, maxLat];
}

/**
 * Build the Mapbox match expression that maps turf status strings to colors.
 */
const STATUS_COLOR_EXPRESSION = [
  'match',
  ['get', 'status'],
  ...Object.entries(TURF_STATUS_DISPLAY).flatMap(([status, { hex }]) => [status, hex]),
  TURF_STATUS_FALLBACK_HEX,
] as unknown as mapboxgl.Expression;

// ---------- Contact Rate Legend ----------

export function ContactRateLegend() {
  const entries = [
    { color: CONTACT_RATE_COLORS.darkGreen, label: '80%+' },
    { color: CONTACT_RATE_COLORS.green, label: '50-80%' },
    { color: CONTACT_RATE_COLORS.yellow, label: '25-50%' },
    { color: CONTACT_RATE_COLORS.red, label: '< 25%' },
    { color: CONTACT_RATE_COLORS.gray, label: 'No data' },
  ];

  return (
    <div className="absolute bottom-6 right-6 z-20 rounded-lg bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm">
      <p className="mb-1 text-xs font-medium text-gray-700">Contact Rate</p>
      <div className="space-y-0.5">
        {entries.map(({ color, label }) => (
          <div key={label} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs text-gray-600">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Component ----------

const TurfMap = forwardRef<TurfMapHandle, TurfMapProps>(function TurfMap(
  {
    token,
    turfs,
    getTurfStatus,
    houses,
    selectedTurfId,
    onTurfClick,
    canvasserPositions,
    drawingEnabled = false,
    initialDrawPolygon,
    onPolygonComplete,
    onPolygonEdit,
    mode = 'management',
    voterPoints,
    showVoterPins = true,
    showTurfs = true,
    showDemographics = false,
    demographicMetric = 'medianIncome',
    showElectionOverlay = false,
    showMajorRoads = false,
    showContactRate = false,
    contactRates,
    onVoterClick,
    children,
  },
  ref,
) {
  const mapRef = useRef<MapRef | null>(null);
  const [nativeMap, setNativeMap] = useState<MapboxMap | null>(null);
  const [hoveredCanvasser, setHoveredCanvasser] = useState<{
    volunteerName: string;
    volunteerInitials: string;
    isStale: boolean;
    lastUpdated: string;
    lngLat: [number, number];
  } | null>(null);

  // ---- Mapbox Draw integration ----

  const { clearPolygon: clearDraw } = useMapboxDraw(nativeMap, {
    enabled: drawingEnabled,
    initialPolygon: initialDrawPolygon,
    onPolygonComplete,
    onPolygonEdit,
  });

  // ---- GeoJSON sources ----

  /** Contact rate lookup: walkbookId → contactRate (0-1) */
  const contactRateMap = useMemo(() => {
    const lookup: Record<string, number> = {};
    if (contactRates?.length) {
      for (const cr of contactRates) {
        lookup[cr.walkbookId] = cr.contactRate;
      }
    }
    return lookup;
  }, [contactRates]);

  /** Turf polygons FeatureCollection */
  const turfGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    return {
      type: 'FeatureCollection',
      features: turfs.map((turf) => ({
        type: 'Feature' as const,
        geometry: turf.polygon as GeoJSON.Polygon,
        properties: {
          id: turf.id,
          name: turf.name,
          status: getTurfStatus(turf.id),
          isSelected: turf.id === selectedTurfId,
          contactRate: contactRateMap[turf.id] ?? null,
        },
      })),
    };
  }, [turfs, getTurfStatus, selectedTurfId, contactRateMap]);

  /** House dots FeatureCollection */
  const houseGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    return {
      type: 'FeatureCollection',
      features: houses.map((house) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: house.coordinate,
        },
        properties: {
          id: house.id,
          isAssigned: house.isAssigned,
          isVisited: house.isAssigned ? 1 : 0,
        },
      })),
    };
  }, [houses]);

  /** Voter map points FeatureCollection */
  const voterGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!voterPoints?.length) {
      return { type: 'FeatureCollection', features: [] };
    }
    return {
      type: 'FeatureCollection',
      features: voterPoints.map((vp) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: vp.coordinate,
        },
        properties: {
          id: vp.id,
          contacted: vp.isContacted ? 1 : 0,
        },
      })),
    };
  }, [voterPoints]);

  // ---- Click handling ----

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      // In cutting mode, let MapboxDraw handle all clicks
      if (mode === 'cutting') return;

      const features = e.features;
      if (!features || features.length === 0) return;

      // Collect ALL voter features at this click point (handles stacked markers)
      const voterFeatures = features.filter(
        (f) => (f.layer?.id === VOTER_DOTS_LAYER || f.layer?.id === VOTER_DOTS_CONTACTED_LAYER)
          && f.properties?.id,
      );
      if (voterFeatures.length > 0 && onVoterClick) {
        const coords = (voterFeatures[0].geometry as GeoJSON.Point).coordinates;
        const voterIds = voterFeatures.map((f) => f.properties!.id as string);
        onVoterClick(voterIds, [coords[0], coords[1]] as [number, number]);
        return;
      }

      const turfFeature = features.find(
        (f) => f.layer?.id === TURF_FILL_LAYER,
      );
      if (turfFeature?.properties?.id) {
        onTurfClick(turfFeature.properties.id as string);
      }
    },
    [onTurfClick, onVoterClick, mode],
  );

  // ---- Cursor change on hover over voter dots ----

  const handleMouseEnter = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = 'pointer';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = '';
    }
    setHoveredCanvasser(null);
  }, []);

  const handleMouseMove = useCallback(
    (e: MapMouseEvent) => {
      if (!mapRef.current) return;
      // The canvassers layer only exists once live tracking has loaded positions (and is absent
      // entirely when SignalR is down). Querying a missing layer throws on every mousemove, so
      // guard against it.
      if (!mapRef.current.getLayer(CANVASSER_LAYER)) {
        setHoveredCanvasser(null);
        return;
      }
      const features = mapRef.current.queryRenderedFeatures(e.point, {
        layers: [CANVASSER_LAYER],
      });
      if (features.length > 0) {
        const f = features[0];
        const coords = (f.geometry as GeoJSON.Point).coordinates;
        setHoveredCanvasser({
          volunteerName: (f.properties?.volunteerName as string) ?? 'Unknown',
          volunteerInitials: (f.properties?.volunteerInitials as string) ?? '?',
          isStale: f.properties?.isStale === true || f.properties?.isStale === 'true',
          lastUpdated: (f.properties?.lastUpdated as string) ?? new Date().toISOString(),
          lngLat: [coords[0], coords[1]],
        });
      } else {
        setHoveredCanvasser(null);
      }
    },
    [],
  );

  // ---- Map load: capture native map ref ----

  const handleMapLoad = useCallback(() => {
    if (mapRef.current) {
      setNativeMap(mapRef.current.getMap() as unknown as MapboxMap);
    }
  }, []);

  // ---- Auto-fit to all turfs on initial load (management mode) ----
  // The hardcoded DEFAULT_MAP_CENTER (Birmingham) is only a pre-load placeholder;
  // once the map is ready and turfs are available, frame them so non-Alabama
  // campaigns don't sit looking at the wrong state.
  const hasAutoFitRef = useRef(false);
  useEffect(() => {
    if (hasAutoFitRef.current) return;
    if (mode !== 'management') return;
    if (!nativeMap || !mapRef.current) return;
    if (turfs.length === 0) return;

    const bounds = computeBounds(turfs);
    if (!bounds) return;

    mapRef.current.fitBounds(bounds, { padding: 60, duration: 0 });
    hasAutoFitRef.current = true;
  }, [nativeMap, turfs, mode]);

  // ---- Imperative methods ----

  useImperativeHandle(ref, () => ({
    fitToTurf(turfId: string) {
      const turf = turfs.find((t) => t.id === turfId);
      if (!turf || !mapRef.current) return;

      const bounds = computeBounds([turf]);
      if (!bounds) return;

      mapRef.current.fitBounds(bounds, { padding: 80 });
    },

    fitToAll() {
      if (turfs.length === 0 || !mapRef.current) return;

      const bounds = computeBounds(turfs);
      if (!bounds) return;

      mapRef.current.fitBounds(bounds, { padding: 60 });
    },

    clearDraw() {
      clearDraw();
    },
  }));

  // ---- Canvasser halo pulse animation ----

  useEffect(() => {
    if (!nativeMap || !canvasserPositions?.features?.length) return;

    let frame: number;
    const animate = () => {
      const phase = (Date.now() % 2000) / 2000;
      const sin = Math.sin(phase * Math.PI * 2);
      const radius = 15 + sin * 5;    // 10–20px
      const opacity = 0.18 + sin * 0.12; // 0.06–0.30

      if (nativeMap.getLayer(CANVASSER_HALO_LAYER)) {
        nativeMap.setPaintProperty(CANVASSER_HALO_LAYER, 'circle-radius', radius);
        nativeMap.setPaintProperty(CANVASSER_HALO_LAYER, 'circle-opacity', opacity);
      }

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [nativeMap, canvasserPositions?.features?.length]);

  // ---- Render ----

  return (
    <div className="relative w-full h-full">
      <Map
        ref={mapRef}
        mapboxAccessToken={token}
        mapStyle={MAPBOX_STYLE}
        initialViewState={DEFAULT_MAP_CENTER}
        interactiveLayerIds={mode === 'cutting' ? undefined : INTERACTIVE_LAYERS}
        onClick={handleMapClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onLoad={handleMapLoad}
        doubleClickZoom={mode !== 'cutting'}
        style={{ width: '100%', height: '100%' }}
        cursor={mode === 'cutting' && drawingEnabled ? 'crosshair' : undefined}
      >
        {/* ---- Navigation controls ---- */}
        <NavigationControl position="top-right" />
        <GeolocateControl
          position="top-right"
          trackUserLocation
          showAccuracyCircle={false}
        />

        {/* ---- Census demographic overlay (below all other layers) ---- */}
        <DemographicOverlay
          visible={showDemographics}
          metric={demographicMetric}
        />

        {/* ---- Election precinct overlay (red/blue choropleth) ---- */}
        <ElectionOverlay visible={showElectionOverlay} />

        {/* ---- OSM major roads overlay (sign placement context) ---- */}
        <RoadClassOverlay visible={showMajorRoads} />

        {/* ---- Turf polygons ---- */}
        <Source id="turfs" type="geojson" data={turfGeoJSON}>
          {/* Fill */}
          <Layer
            id={TURF_FILL_LAYER}
            type="fill"
            layout={{ visibility: showTurfs ? 'visible' : 'none' }}
            paint={{
              'fill-color': showContactRate
                ? [
                    'case',
                    ['==', ['get', 'contactRate'], null], CONTACT_RATE_COLORS.gray,
                    ['step', ['get', 'contactRate'],
                      CONTACT_RATE_COLORS.red,
                      CONTACT_RATE_THRESHOLDS.low, CONTACT_RATE_COLORS.yellow,
                      CONTACT_RATE_THRESHOLDS.medium, CONTACT_RATE_COLORS.green,
                      CONTACT_RATE_THRESHOLDS.high, CONTACT_RATE_COLORS.darkGreen,
                    ],
                  ] as unknown as mapboxgl.Expression
                : STATUS_COLOR_EXPRESSION,
              'fill-opacity': [
                'case',
                ['==', ['get', 'isSelected'], true],
                0.5,
                showContactRate ? 0.35 : 0.12,
              ],
            }}
          />

          {/* Outline */}
          <Layer
            id={TURF_LINE_LAYER}
            type="line"
            layout={{ visibility: showTurfs ? 'visible' : 'none' }}
            paint={{
              'line-color': STATUS_COLOR_EXPRESSION,
              'line-width': [
                'case',
                ['==', ['get', 'isSelected'], true],
                3,
                2,
              ],
            }}
          />

          {/* Labels */}
          <Layer
            id={TURF_LABEL_LAYER}
            type="symbol"
            minzoom={14}
            layout={{
              visibility: showTurfs ? 'visible' : 'none',
              'text-field': showContactRate
                ? [
                    'case',
                    ['==', ['get', 'contactRate'], null],
                    ['get', 'name'],
                    ['concat',
                      ['get', 'name'],
                      ' — ',
                      ['to-string', ['round', ['*', 100, ['get', 'contactRate']]]],
                      '%',
                    ],
                  ] as unknown as mapboxgl.Expression
                : (['get', 'name'] as unknown as mapboxgl.Expression),
              'text-size': 13,
              'text-anchor': 'center',
              'text-allow-overlap': false,
            }}
            paint={{
              'text-color': MAP_COLORS.textDark,
              'text-halo-color': MAP_COLORS.white,
              'text-halo-width': 1.5,
            }}
          />
        </Source>

        {/* ---- Voter points (all voters with coordinates) ---- */}
        <Source id="voter-points" type="geojson" data={voterGeoJSON}>
          {/* Heatmap at low zoom */}
          <Layer
            id={VOTER_HEAT_LAYER}
            type="heatmap"
            maxzoom={15}
            layout={{ visibility: showVoterPins ? 'visible' : 'none' }}
            paint={{
              'heatmap-weight': 1,
              'heatmap-intensity': [
                'interpolate', ['linear'], ['zoom'],
                10, 0.6,
                14, 1.5,
              ],
              'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0,    'rgba(0,0,0,0)',
                0.15, 'rgba(147,197,253,0.4)',
                0.35, 'rgba(96,165,250,0.6)',
                0.55, 'rgba(59,130,246,0.75)',
                0.75, 'rgba(37,99,235,0.85)',
                1,    'rgba(29,78,216,0.95)',
              ],
              'heatmap-radius': [
                'interpolate', ['linear'], ['zoom'],
                10, 8,
                14, 15,
              ],
              'heatmap-opacity': [
                'interpolate', ['linear'], ['zoom'],
                13.5, 0.8,
                15, 0,
              ],
            }}
          />

          {/* Uncontacted voter dots */}
          <Layer
            id={VOTER_DOTS_LAYER}
            type="circle"
            filter={['==', ['get', 'contacted'], 0]}
            minzoom={13}
            layout={{ visibility: showVoterPins ? 'visible' : 'none' }}
            paint={{
              'circle-radius': [
                'interpolate', ['linear'], ['zoom'],
                13, 2,
                16, 4,
              ],
              'circle-color': MAP_COLORS.defaultBlue,
              'circle-opacity': [
                'interpolate', ['linear'], ['zoom'],
                13, 0,
                14, 0.7,
              ],
              'circle-stroke-width': 0.5,
              'circle-stroke-color': MAP_COLORS.white,
              'circle-stroke-opacity': [
                'interpolate', ['linear'], ['zoom'],
                13, 0,
                14, 0.7,
              ],
            }}
          />
          {/* Contacted voter dots — rendered on top */}
          <Layer
            id={VOTER_DOTS_CONTACTED_LAYER}
            type="circle"
            filter={['==', ['get', 'contacted'], 1]}
            minzoom={13}
            layout={{ visibility: showVoterPins ? 'visible' : 'none' }}
            paint={{
              'circle-radius': [
                'interpolate', ['linear'], ['zoom'],
                13, 3,
                16, 5,
              ],
              'circle-color': MAP_COLORS.contactedOrange,
              'circle-opacity': [
                'interpolate', ['linear'], ['zoom'],
                13, 0,
                14, 0.9,
              ],
              'circle-stroke-width': 0.5,
              'circle-stroke-color': MAP_COLORS.white,
              'circle-stroke-opacity': [
                'interpolate', ['linear'], ['zoom'],
                13, 0,
                14, 0.9,
              ],
            }}
          />
        </Source>

        <Source id="houses" type="geojson" data={houseGeoJSON}>
          {/* ---- House dots (high zoom) ---- */}
          <Layer
            id={HOUSES_LAYER}
            type="circle"
            paint={{
              'circle-radius': 5,
              'circle-color': [
                'case',
                ['==', ['get', 'isVisited'], 1],
                MAP_COLORS.contactedOrange,
                MAP_COLORS.defaultBlue,
              ],
              'circle-stroke-width': 1,
              'circle-stroke-color': MAP_COLORS.white,
              'circle-opacity': [
                'interpolate', ['linear'], ['zoom'],
                14.5, 0,
                15.5, 1,
              ],
              'circle-stroke-opacity': [
                'interpolate', ['linear'], ['zoom'],
                14.5, 0,
                15.5, 1,
              ],
            }}
          />
        </Source>

        {/* ---- Canvasser positions ---- */}
        {canvasserPositions && (
          <Source id="canvassers" type="geojson" data={canvasserPositions}>
            {/* Halo / animated pulse ring behind dot — only live (non-stale) canvassers pulse */}
            <Layer
              id={CANVASSER_HALO_LAYER}
              type="circle"
              filter={['!=', ['get', 'isStale'], true]}
              paint={{
                'circle-radius': 15,
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.18,
              }}
            />

            {/* Main canvasser dot */}
            <Layer
              id={CANVASSER_LAYER}
              type="circle"
              paint={{
                'circle-radius': 10,
                'circle-color': ['get', 'color'],
                'circle-stroke-width': 3,
                'circle-stroke-color': MAP_COLORS.white,
              }}
            />

            {/* Canvasser initials on top of dot */}
            <Layer
              id={CANVASSER_LABEL_LAYER}
              type="symbol"
              layout={{
                'text-field': ['get', 'volunteerInitials'],
                'text-size': 10,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{
                'text-color': MAP_COLORS.white,
              }}
            />
          </Source>
        )}

        {/* ---- Canvasser hover popup ---- */}
        {hoveredCanvasser && (
          <CanvasserPopup
            volunteerName={hoveredCanvasser.volunteerName}
            volunteerInitials={hoveredCanvasser.volunteerInitials}
            isStale={hoveredCanvasser.isStale}
            lastUpdated={hoveredCanvasser.lastUpdated}
            lngLat={hoveredCanvasser.lngLat}
          />
        )}

        {/* ---- Contact rate legend ---- */}
        {showContactRate && showTurfs && <ContactRateLegend />}

        {/* ---- Popups / child content ---- */}
        {children}
      </Map>
      {/* Vertex + midpoint disks for turf-cutting — portal-based, sits above the map canvas */}
      <TurfVertexOverlay map={nativeMap} enabled={drawingEnabled === true} />
      {/* Floating overlay legends (outside Map context, no useMap needed) */}
      <RoadClassLegend />
    </div>
  );
});

export default TurfMap;
